import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import BackToDocumentary from "../../shared/BackToDocumentary/BackToDocumentary";
import { featuredMetrics, skills } from "../../../content";
import type { ResolvedMetric } from "../../../types/content";
import Figure from "./Figure";
import RoleTimeline from "./RoleTimeline";
import { certCategoryBars, certYearBars, projectBars, skillBars } from "./figures";
import type { CountBar } from "./figures";

/**
 * ============================================================================
 * Career Dashboard — the /dashboard route.
 * ============================================================================
 *
 * A data view of material that is already on this site: the roles from
 * `experience.json`, the owner-curated headline metrics from `metrics.json`,
 * the skills taxonomy, the credentials the Certifications section publishes,
 * and the build portfolio. It introduces NO new professional fact. Every
 * number is printed through `<Figure>`, which can only render an id registered
 * in `figures.ts` — see that file for why a dashboard is the page shape where
 * an invented figure is hardest to notice.
 *
 * FORM: bars and columns, drawn in CSS, with no charting dependency. Three
 * reasons, in order of weight.
 *
 *   · ACCESSIBILITY IS THE DEFAULT RATHER THAN A RETROFIT. Every chart below
 *     is a list whose rows carry their label and their value AS TEXT. A screen
 *     reader hears "AI, 6 credentials"; the bar is `aria-hidden` decoration
 *     sitting beside the number, not the only place the number exists. No
 *     `role="img"` with a summary nobody can verify, no canvas, no tooltip
 *     holding data the page does not otherwise state.
 *   · THE CONTRAST SWEEP MEASURES TEXT, and text is where the information is.
 *     A chart library that paints values into `<canvas>` or into SVG `<text>`
 *     with its own palette would put every figure on this page outside the
 *     instrument that keeps the rest of the site AA.
 *   · No new dependency, and the visual language is the site's own tokens, so
 *     both themes follow automatically.
 *
 * The bars carry `--cat-*` hues, which is the vocabulary this site already
 * uses where a colour is INFORMATION (D-035, D-061). They are decoration here
 * and are not load-bearing: nothing on this page is distinguishable only by
 * colour, which is the accessibility rule that matters for a chart.
 */

/**
 * The page's standfirst — used verbatim as this route's meta description
 * (D-044), so the search result cannot drift from the page's opening line.
 *
 * IT MAKES NO CAREER CLAIM. D-066's rule: a standfirst ships in the
 * `<meta name="description">` and is the one line on a page nothing checks, and
 * the martial-arts page opened with "two decades" over a three-year record. So
 * this sentence describes the PAGE, which is a thing the page can be checked
 * against, and leaves every professional assertion to the figures — which are
 * checked. Counts belong to the layer and `routeMeta` computes them.
 */
export const DASHBOARD_STANDFIRST =
	"The same record the rest of this site sets in prose, drawn instead — roles and tenure, the delivery figures, the skills taxonomy, the credentials and the build portfolio, each one read from the content layer rather than typed here.";

/** Section heading. Every one of these is an <h2>; the route's single <h1> is the page title. */
const Panel = ({
	id,
	title,
	blurb,
	children
}: {
	id: string;
	title: string;
	blurb: string;
	children: React.ReactNode;
}): JSX.Element => (
	<section className="mt-14" aria-labelledby={id}>
		{/* The brand-ramp hairline that marks a card section everywhere else on
		    this site (D-099), used here to mark a PANEL. This page was the
		    flattest surface left: bars and columns on the page ground with a
		    plain heading over them, which reads as a report rather than as part
		    of the same document. */}
		<span
			aria-hidden="true"
			className="mb-4 block h-[3px] w-10 rounded-full bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]"
		/>
		<h2 id={id} className="text-xl font-semibold text-[var(--text-strong)] sm:text-2xl">
			{title}
		</h2>
		<p className="mt-2 max-w-[70ch] text-sm leading-relaxed text-[var(--text-muted)]">{blurb}</p>
		<div className="mt-6">{children}</div>
	</section>
);

/**
 * A horizontal bar row. The label and the count are text; the bar is decoration.
 *
 * `flex-1` on the track and `shrink-0` on the two text cells is what keeps this
 * from overflowing at 390px — the bar absorbs the remaining width instead of
 * the row growing to fit a long category name (D-084: over-wide content is
 * HIDDEN by the page's `overflow-x: hidden`, not prevented by it).
 */
const BarRow = ({ bar, max, hue, unit }: { bar: CountBar; max: number; hue: string; unit: string }): JSX.Element => (
	<li className="flex items-center gap-3 py-1.5">
		<span className="w-[9.5rem] shrink-0 text-xs text-[var(--text-secondary)] sm:w-56 sm:text-sm">{bar.label}</span>
		<span aria-hidden="true" className="hidden h-2.5 flex-1 rounded-full bg-[var(--border-soft)] sm:block">
			<span
				className="block h-full rounded-full"
				style={{
					width: `${Math.max(3, (bar.count / max) * 100)}%`,
					backgroundColor: `var(--cat-${hue}-text)`
				}}
			/>
		</span>
		<span className="ml-auto shrink-0 text-sm font-semibold tabular-nums text-[var(--text-primary)] sm:ml-0">
			<Figure id={bar.figureId} />
			<span className="ml-1 text-xs font-normal text-[var(--text-muted)]">{unit}</span>
		</span>
	</li>
);

export default function Dashboard(): JSX.Element {
	const reduceMotion = useReducedMotion();

	const maxSkill = Math.max(...skillBars.map((bar: CountBar) => bar.count));
	const maxCertCategory = Math.max(...certCategoryBars.map((bar: CountBar) => bar.count));
	const maxCertYear = Math.max(...certYearBars.map((bar: CountBar) => bar.count));
	const maxProject = Math.max(...projectBars.map((bar: CountBar) => bar.count));

	return (
		<section className="section-shell" aria-labelledby="dashboard-heading">
			<div className="section-rail">
				<p className="section-eyebrow">The Record, Drawn</p>

				{/* THE ROUTE'S ONLY <h1>. Every route ships exactly one (D-042). */}
				<h1 id="dashboard-heading" className="heading">
					Career{" "}
					<span className="bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)] bg-clip-text text-transparent">
						Dashboard
					</span>
				</h1>

				<p className="section-standfirst">{DASHBOARD_STANDFIRST}</p>

				{/* ---------------------------------------------------------- KPIs -- */}
				<Panel
					id="dashboard-metrics"
					title="Delivery in numbers"
					blurb="The headline figures, exactly as the layer records them. Which ones lead is an owner ruling carried in the data, not a choice this page makes."
				>
					{/* `auto-rows-fr` for the reason every other grid on this site
					    gained it (D-099): a grid row stretches its items to the tallest
					    of THAT row, so cards matched their row-mates and not the rows
					    above or below — and these eight carry `context` lines of very
					    different lengths. */}
					<ul className="grid auto-rows-fr grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
						{featuredMetrics.map((metric: ResolvedMetric, index: number) => (
							<motion.li
								key={metric.id}
								className="card-shell"
								initial={reduceMotion ? false : { opacity: 0, y: 24 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true, amount: 0.2 }}
								transition={{
									duration: 0.5,
									delay: Math.min(index * 0.05, 0.24),
									ease: [0.22, 1, 0.36, 1]
								}}
							>
								<div className="card-face flex h-full flex-col p-5">
									<span
										aria-hidden="true"
										className="mb-4 block h-[3px] w-10 rounded-full bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]"
									/>
									<Figure
										id={`metric:${metric.id}`}
										className="text-3xl font-semibold leading-none text-[var(--text-strong)]"
									/>
									<span className="mt-3 text-sm font-semibold text-[var(--text-secondary)]">
										{metric.label}
									</span>
									<span className="mt-2 text-xs leading-relaxed text-[var(--text-muted)]">
										{metric.context}
									</span>
								</div>
							</motion.li>
						))}
					</ul>
				</Panel>

				{/* ------------------------------------------------------ timeline -- */}
				<Panel
					id="dashboard-timeline"
					title="Career timeline"
					blurb="Roles newest first, each bar drawn to its own length. Tenure is counted inclusively from the dates on the entry, and the open-ended one is measured to the build rather than to the clock. Every card opens the full account in Experience."
				>
					{/* THE WHOLE CARD IS ONE LINK. A role card carries no other
					    interactive content, so a wrapping <Link> is one tab stop and one
					    entry in the accessibility tree — D-081 needed a stretched overlay
					    on the workshop cards only because those carry links of their own,
					    and reaching for that here would add machinery to solve a problem
					    this card does not have. */}
					<RoleTimeline to="/#experience" />
					<p className="mt-4 text-xs text-[var(--text-muted)]">
						<Figure id="roles:total" className="font-semibold text-[var(--text-secondary)]" /> roles on the
						record. The full account of each one, with its outcomes, is in{" "}
						<Link
							to="/#experience"
							className="text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
						>
							Experience
						</Link>
						.
					</p>
				</Panel>

				{/* -------------------------------------------------------- skills -- */}
				<Panel
					id="dashboard-skills"
					title="Skills by discipline"
					blurb="The taxonomy as the layer groups it. A count is a count of named skills in that group, not a proficiency score — this site has no data for a proficiency score and does not draw one."
				>
					<ul>
						{skillBars.map((bar: CountBar) => (
							<BarRow key={bar.key} bar={bar} max={maxSkill} hue="violet" unit="skills" />
						))}
					</ul>
					<p className="mt-4 text-xs text-[var(--text-muted)]">
						<Figure id="skills:total" className="font-semibold text-[var(--text-secondary)]" /> named skills
						across <Figure id="skills:categories" className="font-semibold text-[var(--text-secondary)]" />{" "}
						disciplines, of which{" "}
						<Figure id="skills:top" className="font-semibold text-[var(--text-secondary)]" /> are carried as
						top skills: {skills.topSkills.join(", ")}.
					</p>
				</Panel>

				{/* -------------------------------------------------- certifications -- */}
				<Panel
					id="dashboard-credentials"
					title="Credentials"
					blurb="Counted exactly as the Certifications section publishes them, so this page and that one cannot disagree. Accredited certifications are separated from courses and workshops there, and that split is kept here."
				>
					<div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
						<div>
							<h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
								By category
							</h3>
							<ul className="mt-3">
								{certCategoryBars.map((bar: CountBar) => (
									<BarRow
										key={bar.key}
										bar={bar}
										max={maxCertCategory}
										hue="cyan"
										unit="credentials"
									/>
								))}
							</ul>
						</div>

						<div>
							<h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
								By year earned
							</h3>
							{/* EVERY YEAR IN THE RANGE, INCLUDING THE EMPTY ONES. Omitting
							    2021 and 2022 would draw a steady climb over a record that
							    has a two-year gap in it — a shape asserting something the
							    data does not say. */}
							<ul className="mt-3 flex items-end gap-2 sm:gap-3">
								{certYearBars.map((bar: CountBar) => (
									<li key={bar.key} className="flex flex-1 flex-col items-center gap-2">
										<span className="text-sm font-semibold tabular-nums text-[var(--text-primary)]">
											<Figure id={bar.figureId} />
										</span>
										<span
											aria-hidden="true"
											className="flex h-28 w-full items-end rounded-md bg-[var(--border-soft)]"
										>
											<span
												className="w-full rounded-md bg-[var(--cat-amber-text)]"
												style={{ height: `${(bar.count / maxCertYear) * 100}%` }}
											/>
										</span>
										<span className="text-[0.6875rem] tabular-nums text-[var(--text-muted)]">
											{bar.label}
										</span>
									</li>
								))}
							</ul>
						</div>
					</div>

					<p className="mt-6 text-xs leading-relaxed text-[var(--text-muted)]">
						<Figure id="certs:total" className="font-semibold text-[var(--text-secondary)]" /> credentials
						published —{" "}
						<Figure id="certs:tier:certification" className="font-semibold text-[var(--text-secondary)]" />{" "}
						accredited certifications and{" "}
						<Figure id="certs:tier:training" className="font-semibold text-[var(--text-secondary)]" />{" "}
						courses and workshops. Each card, with its scan or its issuer link, is in{" "}
						<Link
							to="/#certifications"
							className="text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
						>
							Certifications
						</Link>
						.
					</p>
				</Panel>

				{/* ----------------------------------------------------- portfolio -- */}
				<Panel
					id="dashboard-portfolio"
					title="Delivery portfolio"
					blurb="What was built, grouped the way the Projects page groups it, beside the product suite it sits alongside."
				>
					<ul>
						{projectBars.map((bar: CountBar) => (
							<BarRow key={bar.key} bar={bar} max={maxProject} hue="teal" unit="builds" />
						))}
					</ul>
					<p className="mt-4 text-xs leading-relaxed text-[var(--text-muted)]">
						<Figure id="projects:total" className="font-semibold text-[var(--text-secondary)]" /> builds in{" "}
						<Link
							to="/projects"
							className="text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
						>
							Projects
						</Link>
						, <Figure id="products:total" className="font-semibold text-[var(--text-secondary)]" />{" "}
						platforms in{" "}
						<Link
							to="/products"
							className="text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
						>
							Products
						</Link>
						, and <Figure id="workshops:total" className="font-semibold text-[var(--text-secondary)]" />{" "}
						sessions in{" "}
						<Link
							to="/workshops"
							className="text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
						>
							Workshops &amp; Training
						</Link>
						.
					</p>
				</Panel>

				<div className="mt-16 flex flex-wrap justify-center gap-6">
					<Link
						to="/ai-innovation"
						className="inline-block min-h-[24px] text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
					>
						AI &amp; Innovation →
					</Link>
					<BackToDocumentary />
				</div>
			</div>
		</section>
	);
}
